import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Menu, 
  Button, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Space, 
  Dropdown,
  Avatar,
  Modal,
  Form,
  Input,
  InputNumber,
  Divider,
  Tabs,
  Tag,
  Alert,
  Timeline
} from 'antd';
import { App as AntdApp } from 'antd';
import { 
  HomeOutlined, 
  CalculatorOutlined, 
  HistoryOutlined, 
  BookOutlined,
  UserOutlined, 
  DownOutlined,
  WechatOutlined,
  MailOutlined,
  LockOutlined,
  SettingOutlined,
  UserSwitchOutlined,
  ToolOutlined,
  SafetyOutlined,
  LogoutOutlined,
  InfoCircleOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { HashRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { processOfflineQueue } from './utils/syncService';
import { listQueue, clearQueue, removeById } from './utils/offlineQueue';
import './App.less';
import HomePage from './pages/HomePage';
import CalculationPage from './pages/CalculationPage';
import HistoryPage from './pages/HistoryPage';
import KnowledgePage from './pages/KnowledgePage';
import ShareView from './pages/ShareView';
import DeviceLibraryPage from './pages/DeviceLibraryPage';
import ManualPage from './pages/Manual';
import { useI18n } from './i18n/i18n';
import LanguageSwitch from './components/LanguageSwitch';
import CHANGELOG from './utils/changelog';
import { getAiOptions, saveAiOptions } from './utils/aiClient';
import { Analytics } from '@vercel/analytics/react';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;


const App = () => {
  const { t } = useI18n();
  const { message } = AntdApp.useApp();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const [wechatQRVisible, setWechatQRVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  // 系统设置相关状态
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [settingsActiveTab, setSettingsActiveTab] = useState('general');
  const [generalForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [calculationForm] = Form.useForm();
  const [aiForm] = Form.useForm();
  // 短信发送与倒计时
  const [smsSending, setSmsSending] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
  const appTitle = import.meta.env.VITE_APP_TITLE || '继电保护计算平台';

  // 在线/离线状态
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  // 离线队列状态
  const [queueCount, setQueueCount] = useState(0);
  const [queueItems, setQueueItems] = useState([]);
  const [queueModalVisible, setQueueModalVisible] = useState(false);

  useEffect(() => {
    const updateStatus = () => setOnline(typeof navigator !== 'undefined' ? navigator.onLine : true);
    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
    };
  }, []);

  // 刷新离线队列计数
  const refreshQueue = () => {
    try {
      const items = listQueue();
      setQueueItems(items);
      setQueueCount(items.length);
    } catch (_) {}
  };

  useEffect(() => {
    refreshQueue();
    const timer = setInterval(refreshQueue, 3000);
    const onVisible = () => { if (typeof document !== 'undefined' && document.visibilityState === 'visible') refreshQueue(); };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }
    return () => {
      clearInterval(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, []);

  // 监听离线队列事件，实时刷新计数
  useEffect(() => {
    const onQueueUpdated = (e) => {
      try {
        const items = e && e.detail && Array.isArray(e.detail.items) ? e.detail.items : null;
        if (items) {
          setQueueItems(items);
          setQueueCount(items.length);
        } else {
          refreshQueue();
        }
      } catch (_) {
        refreshQueue();
      }
    };
    try { window.addEventListener('offlineQueue:updated', onQueueUpdated); } catch (_) {}
    return () => { try { window.removeEventListener('offlineQueue:updated', onQueueUpdated); } catch (_) {} };
  }, []);
  const handleOpenQueueModal = () => { refreshQueue(); setQueueModalVisible(true); };
  const handleClearQueue = () => { clearQueue(); refreshQueue(); message.success(t('offline.modal.cleared_success')); };
  const handleRemoveQueueItem = (id) => { removeById(id); refreshQueue(); message.success(t('offline.modal.removed_success')); };
  const formatQueueType = (type) => {
    const map = {
      save_calculation: t('offline.type.save'),
      update_meta: t('offline.type.update'),
      delete_calculation: t('offline.type.delete')
    };
    return map[type] || type;
  };
  const formatQueueName = (item) => {
    try {
      if (item.type === 'save_calculation') {
        const name = item.payload?.result?.name || '';
        return name ? `${t('offline.modal.name_label')}: ${name}` : t('offline.modal.name_label');
      }
      const id = item.payload?.id;
      return id ? `${t('offline.modal.id_label')}: ${id}` : '';
    } catch (_) {
      return '';
    }
  };

  // 检查用户登录状态
  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user || null);
        if (session?.user) {
          ensureProfile(session.user);
          ensureUsersRecord(session.user);
        }
        
        // 监听认证状态变化
        supabase.auth.onAuthStateChange((_event, session) => {
          setUser(session?.user || null);
          if (session?.user) {
            ensureProfile(session.user);
            ensureUsersRecord(session.user);
          }
        });
      } catch (error) {
        console.error('检查用户状态失败:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  // 微信登录处理
  const handleWechatLogin = async () => {
    try {
      message.info('微信登录功能演示');
      setWechatQRVisible(true);
    } catch (error) {
      if (error) {
        console.error('微信登录失败:', error.message || error);
        message.error('微信登录失败: ' + (error.message || '未知错误'));
      } else {
        console.error('微信登录失败: 未知错误');
        message.error('微信登录失败: 未知错误');
      }
    }
  };

  // 启动验证码倒计时
  const startSmsCountdown = (seconds = 60) => {
    setSmsCountdown(seconds);
    const timer = setInterval(() => {
      setSmsCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 发送短信验证码（可配置API）
  const sendSmsCode = async () => {
    try {
      const phoneFmt = generalForm.getFieldValue('phone') || '';
      const phone = (phoneFmt || '').replace(/\s/g, '');
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        message.error('请先输入正确的手机号码');
        return;
      }
      if (smsCountdown > 0 || smsSending) return;

      setSmsSending(true);
      const smsApiUrl = import.meta.env.VITE_SMS_API_URL || '/api/send-sms-code';
      const resp = await fetch(smsApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, scene: 'profile_bind' })
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(text || `发送失败(${resp.status})`);
      }
      message.success('验证码已发送');
      startSmsCountdown(60);
    } catch (e) {
      message.error(`发送验证码失败: ${e.message || e}`);
    } finally {
      setSmsSending(false);
    }
  };

  // 确保存在个人资料（首次登录自动补建）
  const ensureProfile = async (currentUser) => {
    if (!currentUser?.id) return;
    try {
      const { data: existing, error: qErr } = await supabase
        .from('profiles_23813')
        .select('user_id')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      if (qErr && qErr.code !== 'PGRST116') return; // 忽略 not found 错误
      if (existing) return; // 已存在

      const nicknameFallback = currentUser.user_metadata?.nickname || (currentUser.email ? currentUser.email.split('@')[0] : '用户');
      await supabase
        .from('profiles_23813')
        .insert([{ user_id: currentUser.id, nickname: nicknameFallback, company: null, phone: null }]);

      // 首次创建资料后，提示并引导完善资料
      message.info('欢迎首次登录，请完善个人资料');
      setSettingsActiveTab('general');
      setSettingsModalVisible(true);
    } catch (_) {
      // 静默失败：不阻塞登录流程
    }
  };

  // 确保 users_23813 中存在与 Supabase 用户的映射记录（用于 calculations_23813 外键）
  const ensureUsersRecord = async (currentUser) => {
    if (!currentUser?.id) return;
    try {
      const { data: existing, error: qErr } = await supabase
        .from('users_23813')
        .select('id')
        .eq('id', currentUser.id)
        .maybeSingle();

      // 忽略 not found 错误码（PGRST116）
      if (qErr && qErr.code !== 'PGRST116') return;
      if (existing) return; // 已存在则跳过

      const nicknameFallback = currentUser.user_metadata?.nickname || (currentUser.email ? currentUser.email.split('@')[0] : '用户');
      const avatarFallback = currentUser.user_metadata?.avatar_url || '';
      const openid = `auth:${currentUser.id}`; // 保证唯一性

      const { error: insertErr } = await supabase
        .from('users_23813')
        .insert([{ id: currentUser.id, openid, nickname: nicknameFallback, avatar_url: avatarFallback }]);

      if (insertErr) {
        console.error('创建 users_23813 失败:', insertErr.message);
      }

      // 同步创建身份映射：supabase
      const { error: identityErr } = await supabase
        .from('user_identities_23813')
        .upsert({
          user_id: currentUser.id,
          provider: 'supabase',
          provider_user_id: currentUser.id,
          extra: currentUser.user_metadata || null,
        }, { onConflict: 'provider,provider_user_id' });

      if (identityErr) {
        console.error('创建/更新身份映射失败:', identityErr.message);
      }
    } catch (e) {
      console.error('确保 users_23813 记录异常:', e);
    }
  };

  // 邮箱登录处理
  const handleEmailLogin = async (values) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      if (error) {
        message.error('登录失败: ' + error.message);
        return;
      }

      if (data?.user) {
        setUser(data.user);
        // 首次登录补建资料
        ensureProfile(data.user);
        // 建立 users_23813 映射记录，避免保存计算历史外键冲突
        ensureUsersRecord(data.user);
        setLoginModalVisible(false);
        loginForm.resetFields();
        message.success('登录成功');
      } else {
        message.error('登录失败: 未返回用户信息');
      }
    } catch (error) {
      console.error('登录异常:', error);
      message.error('登录异常: ' + (error.message || '未知错误'));
    }
  };

  // 注册处理
  const handleRegister = async (values) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            nickname: values.nickname || '',
          },
        },
      });

      if (error) {
        message.error('注册失败: ' + error.message);
        return;
      }

      if (data?.user) {
        // 将昵称等资料写入 profiles_23813（若违反唯一性将返回错误）
        try {
          const { error: profileError } = await supabase
            .from('profiles_23813')
            .insert([
              {
                user_id: data.user.id,
                nickname: values.nickname,
                company: '',
                phone: null,
              },
            ]);

          if (profileError) {
            message.warning('资料写入提示: ' + profileError.message);
          }
        } catch (e) {
          // 忽略资料写入异常，不影响注册流程
        }

        message.success('注册成功，请查看邮箱确认');
        setLoginModalVisible(false);
        registerForm.resetFields();
      } else {
        message.error('注册失败: 未返回用户信息');
      }
    } catch (error) {
      console.error('注册异常:', error);
      message.error('注册异常: ' + (error.message || '未知错误'));
    }
  };

  // 登出处理
  const handleLogout = async () => {
    try {
      // 本地优先清理，避免路由切换导致请求中止产生无害报错
      setUser(null);
      message.success('已登出');
      navigate('/');

      // 后台触发服务端登出，静默处理网络中止/失败
      Promise.resolve()
        .then(() => supabase.auth.signOut())
        .then((res) => {
          if (res?.error) {
            const msg = res.error?.message || '';
            // 忽略被中止等非关键错误
            if (!/abort|ERR_ABORTED/i.test(msg)) {
              console.warn('后台登出提示:', msg);
            }
          }
        })
        .catch((err) => {
          const msg = err?.message || String(err || '');
          if (!/abort|ERR_ABORTED/i.test(msg)) {
            console.warn('后台登出异常:', msg);
          }
        });
    } catch (error) {
      console.error('登出异常:', error);
      message.error('登出异常: ' + (error.message || '未知错误'));
    }
  };

  // 系统设置处理函数
  const handleSettingsClick = (key) => {
    setSettingsActiveTab(key);
    setSettingsModalVisible(true);
  };

  // 当用户或设置弹窗状态变化时，同步通用设置表单的字段，避免跨用户显示相同信息
  useEffect(() => {
    if (settingsModalVisible && settingsActiveTab === 'general') {
      generalForm.setFieldsValue({
        nickname: user?.user_metadata?.nickname || '',
        company: user?.user_metadata?.company || '',
        phone: user?.user_metadata?.phone || '',
        smsCode: '',
      });
    }
  }, [user, settingsModalVisible, settingsActiveTab, generalForm]);

  // 当切换到 AI 设置页时，加载已保存的 AI 配置
  useEffect(() => {
    if (settingsModalVisible && settingsActiveTab === 'ai') {
      const opts = getAiOptions();
      aiForm.setFieldsValue(opts);
    }
  }, [settingsModalVisible, settingsActiveTab, aiForm]);

  // 通用设置提交（唯一性校验 + upsert）
  const handleGeneralSettings = async (values) => {
    try {
      const { nickname, company } = values;
      // 去空格后的手机号
      const phone = (values.phone || '').replace(/\s/g, '');
      // 短信验证码改为可选：不再强制要求填写

      // 1) 唯一性校验（排除自己）
      const { data: nickExists, error: nickErr } = await supabase
        .from('profiles_23813')
        .select('user_id')
        .eq('nickname', nickname)
        .neq('user_id', user.id)
        .maybeSingle();

      if (nickErr && nickErr.code !== 'PGRST116') {
        // 忽略 not found 错误（maybeSingle）
        message.error('昵称校验失败: ' + nickErr.message);
        return;
      }
      if (nickExists) {
        message.error('该昵称已被占用');
        return;
      }

      if (phone) {
        const { data: phoneExists, error: phoneErr } = await supabase
          .from('profiles_23813')
          .select('user_id')
          .eq('phone', phone)
          .neq('user_id', user.id)
          .maybeSingle();
        if (phoneErr && phoneErr.code !== 'PGRST116') {
          message.error('手机号校验失败: ' + phoneErr.message);
          return;
        }
        if (phoneExists) {
          message.error('该手机号已被占用');
          return;
        }
      }

      // 2) upsert 到 profiles_23813
      const { error: upsertErr } = await supabase
        .from('profiles_23813')
        .upsert({
          user_id: user.id,
          nickname,
          company: company || null,
          phone: phone || null,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });
      if (upsertErr) {
        message.error('资料保存失败: ' + upsertErr.message);
        return;
      }

      // 3) 同步到 auth user_metadata（可选但便于前端显示）
      const { error: authErr } = await supabase.auth.updateUser({
        data: { nickname, company, phone }
      });
      if (authErr) {
        message.warning('资料已保存，但同步昵称到用户资料时出错: ' + authErr.message);
      }

      // 更新本地用户状态
      setUser((prev) => prev ? {
        ...prev,
        user_metadata: {
          ...prev.user_metadata,
          nickname,
          company,
          phone,
        }
      } : prev);

      message.success('设置保存成功');
      setSettingsModalVisible(false);
    } catch (error) {
      message.error('设置保存失败: ' + (error.message || '未知错误'));
    }
  };

  // 密码修改提交
  const handlePasswordChange = async (values) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword
      });

      if (error) {
        message.error('密码修改失败: ' + error.message);
        return;
      }

      message.success('密码修改成功');
      setSettingsModalVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      message.error('密码修改异常: ' + error.message);
    }
  };

  // 计算参数设置提交
  const handleCalculationSettings = async (values) => {
    try {
      // 这里可以调用API保存计算参数设置
      console.log('计算参数设置:', values);
      message.success('计算参数设置保存成功');
      setSettingsModalVisible(false);
    } catch (error) {
      message.error('设置保存失败: ' + error.message);
    }
  };

  // AI 模型参数设置提交
  const handleAiSettings = async (values) => {
    try {
      const saved = saveAiOptions(values);
      console.log('AI 设置已保存:', saved);
      message.success(t('ai.settings.save_success') || 'AI 模型参数已保存');
      setSettingsModalVisible(false);
    } catch (error) {
      message.error('设置保存失败: ' + (error.message || '未知错误'));
    }
  };

  // 获取菜单项
  const getMenuItems = () => [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: t('menu.home'),
    },
    {
      key: '/calculation',
      icon: <CalculatorOutlined />,
      label: t('menu.calc'),
    },
    {
      key: '/history',
      icon: <HistoryOutlined />,
      label: t('menu.history'),
    },
    // 仅登录用户显示设备库菜单，且位置在“历史”之后
    ...(user ? [{ key: '/device-library', icon: <ToolOutlined />, label: t('menu.device_library') }] : []),
    {
      key: '/knowledge',
      icon: <BookOutlined />,
      label: t('menu.knowledge'),
    },
    {
      key: '/manual',
      icon: <InfoCircleOutlined />,
      label: '产品说明书',
    },
  ];

  // 处理菜单点击
  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  // 处理登录表单提交
  const handleLoginSubmit = (values) => {
    if (values.email && values.password) {
      handleEmailLogin(values);
    }
  };

  // 处理注册表单提交
  const handleRegisterSubmit = (values) => {
    if (values.email && values.password && values.confirmPassword) {
      if (values.password !== values.confirmPassword) {
        message.error('两次输入的密码不一致');
        return;
      }
      handleRegister(values);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Card style={{ width: 300, textAlign: 'center' }}>
          <Space direction="vertical">
            <CalculatorOutlined style={{ fontSize: 32, color: '#1890FF' }} />
            <Text>{t('loading.text')}</Text>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <Layout className="layout">
      <Header className="header">
        <div className="logo">
          <CalculatorOutlined style={{ fontSize: 24, color: '#fff' }} />
          <Title level={4} style={{ color: '#fff', margin: 0, marginLeft: 8 }}>
            {t('app.title')}
          </Title>
        </div>
        
        <Menu
          className="menu"
          theme="dark"
          mode="horizontal"
          selectedKeys={[location.pathname]}
          items={getMenuItems()}
          onClick={handleMenuClick}
        />
        
        <div className="user-actions">
          <Tag color={online ? 'green' : 'red'} style={{ marginRight: 12 }}>
            {online ? t('status.online') : t('status.offline')}
          </Tag>
          {(!online || queueCount > 0) && (
            <Button size="small" onClick={handleOpenQueueModal} style={{ marginRight: 12 }}>
              {t('offline.view_queue')}{queueCount > 0 ? ` (${queueCount})` : ''}
            </Button>
          )}
          {!online && (
            <Button size="small" onClick={() => processOfflineQueue()} style={{ marginRight: 12 }}>
              {t('offline.retry_sync')}
            </Button>
          )}
          <LanguageSwitch style={{ marginRight: 12 }} />
          {user ? (
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'general',
                    label: t('settings.general'),
                    icon: <SettingOutlined />,
                    children: [
                      {
                        key: 'profile',
                        label: '个人信息设置',
                        icon: <UserSwitchOutlined />,
                        onClick: () => handleSettingsClick('general'),
                      },
                      {
                        key: 'password',
                        label: t('settings.password'),
                        icon: <SafetyOutlined />,
                        onClick: () => handleSettingsClick('password'),
                      },
                    ],
                  },
                  {
                    key: 'ai',
                    label: t('settings.ai') || 'AI 模型设置',
                    icon: <RobotOutlined />,
                    onClick: () => handleSettingsClick('ai'),
                  },
                  {
                    key: 'calculation',
                    label: t('settings.calculation'),
                    icon: <ToolOutlined />,
                    onClick: () => handleSettingsClick('calculation'),
                  },
                  {
                    key: 'about',
                    label: t('settings.about'),
                    icon: <InfoCircleOutlined />,
                    onClick: () => handleSettingsClick('about'),
                  },
                  {
                    key: 'logout',
                    label: t('logout'),
                    icon: <LogoutOutlined />,
                    onClick: handleLogout,
                    
                  },
                ],
              }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button type="text" className="user-button">
                <Space>
                  <Avatar icon={<UserOutlined />} size="small" />
                  <span className="login-button" >{user.user_metadata?.nickname || user.email}</span>
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          ) : (
            <Button 
              type="primary" 
              onClick={() => {
                setActiveTab('login');
                setLoginModalVisible(true);
              }}
              icon={<UserOutlined />}
              className="login-button"
            >
              {t('auth.login_register')}
            </Button>
          )}
        </div>
      </Header>
      
      <Content className="content">
        {!online && (
          <div style={{ marginBottom: 8 }}>
            <Alert type="warning" showIcon message={t('offline.banner')} />
          </div>
        )}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/calculation" element={<CalculationPage userId={user?.id} />} />
          <Route path="/history" element={<HistoryPage userId={user?.id} />} />
          <Route path="/knowledge" element={<KnowledgePage />} />
          <Route path="/manual" element={<ManualPage />} />
          {/* 未登录访问设备库时给出提示 */}
          <Route path="/device-library" element={user ? <DeviceLibraryPage /> : <Alert type="warning" showIcon message="请先登录后使用设备库" />} />
          <Route path="/share" element={<ShareView />} />
        </Routes>
      </Content>
      
      <Footer className="footer">
        <Text type="secondary">© {new Date().getFullYear()} 电力继电保护计算平台 - 基于IEC 60255标准</Text>
      </Footer>
      
      {/* 离线队列查看模态框 */}
      <Modal
        title={t('offline.modal.title')}
        open={queueModalVisible}
        onCancel={() => setQueueModalVisible(false)}
        footer={null}
        width={560}
      >
        <Card>
          {queueItems.length === 0 ? (
            <Text type="secondary">{t('offline.modal.no_items')}</Text>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {queueItems.map((item) => (
                <Row key={item.id} align="middle" justify="space-between">
                  <Col flex="auto">
                    <Space direction="vertical" size={0}>
                      <Text strong>{formatQueueType(item.type)}</Text>
                      <Text type="secondary">{formatQueueName(item)}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>{new Date(item.ts).toLocaleString()}</Text>
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <Button size="small" onClick={() => handleRemoveQueueItem(item.id)}>{t('offline.modal.remove')}</Button>
                    </Space>
                  </Col>
                </Row>
              ))}
              <Divider />
              <Space>
                <Button danger onClick={handleClearQueue}>{t('offline.modal.clear')}</Button>
                {!online && (
                  <Button type="primary" onClick={async () => { await processOfflineQueue(); refreshQueue(); }}>
                    {t('offline.retry_sync')}
                  </Button>
                )}
              </Space>
            </Space>
          )}
        </Card>
      </Modal>
      
      {/* 登录/注册模态框 */}
      <Modal
        title={t('auth.title')}
        open={loginModalVisible}
        onCancel={() => setLoginModalVisible(false)}
        footer={null}
        className="auth-modal"
      >
        <Card>
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            centered
            items={[
              {
                key: 'login',
                label: t('auth.title'),
                children: (
                  <Form
                    form={loginForm}
                    layout="vertical"
                    onFinish={handleLoginSubmit}
                  >
                    <Form.Item
                      name="email"
                      rules={[
                        { required: true, message: '请输入邮箱!' },
                        { type: 'email', message: '请输入有效的邮箱地址!' }
                      ]}
                    >
                      <Input prefix={<MailOutlined />} placeholder={t('auth.email')} />
                    </Form.Item>
                    
                    <Form.Item
                      name="password"
                      rules={[{ required: true, message: '请输入密码!' }]}
                    >
                      <Input.Password prefix={<LockOutlined />} placeholder={t('auth.password')} />
                    </Form.Item>
                    
                    <Form.Item>
                      <Button type="primary" htmlType="submit" block disabled={!online}>
                        {t('auth.login')}
                      </Button>
                    </Form.Item>
                  </Form>
                )
              },
              {
                key: 'register',
                label: t('auth.register'),
                children: (
                  <Form
                    form={registerForm}
                    layout="vertical"
                    onFinish={handleRegisterSubmit}
                  >
                    <Form.Item
                      name="nickname"
                      label="昵称"
                      rules={[{ required: true, message: '请输入昵称!' }]}
                    >
                      <Input placeholder="请输入昵称" />
                    </Form.Item>
    
                    <Form.Item
                      name="email"
                      rules={[
                        { required: true, message: '请输入邮箱!' },
                        { type: 'email', message: '请输入有效的邮箱地址!' }
                      ]}
                    >
                      <Input prefix={<MailOutlined />} placeholder={t('auth.email')} />
                    </Form.Item>
                    
                    <Form.Item
                      name="password"
                      rules={[
                        { required: true, message: '请输入密码!' },
                        { min: 6, message: '密码至少6位!' }
                      ]}
                    >
                      <Input.Password prefix={<LockOutlined />} placeholder={t('auth.password')} />
                    </Form.Item>
                    
                    <Form.Item
                      name="confirmPassword"
                      rules={[
                        { required: true, message: '请确认密码!' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('两次输入的密码不一致!'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password prefix={<LockOutlined />} placeholder={t('auth.confirm_password')} />
                    </Form.Item>
                    
                    <Form.Item>
                      <Button type="primary" htmlType="submit" block disabled={!online}>
                        {t('auth.register')}
                      </Button>
                    </Form.Item>
                  </Form>
                )
              }
            ]}
          />
          
          <Divider>{t('auth.or')}</Divider>
          
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button 
              block 
              onClick={handleWechatLogin}
              icon={<WechatOutlined />}
              disabled={!online}
            >
              微信登录
            </Button>
          </Space>
        </Card>
      </Modal>
      
      {/* 微信二维码模态框 */}
      <Modal
        title={t('wechat.title')}
        open={wechatQRVisible}
        onCancel={() => setWechatQRVisible(false)}
        footer={null}
      >
        <div style={{ textAlign: 'center' }}>
          <Card>
            <Text>请使用微信扫描下方二维码登录</Text>
            <div style={{ padding: 20 }}>
              <div style={{ 
                width: 200, 
                height: 200, 
                backgroundColor: '#f0f0f0',
                margin: '20px auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <WechatOutlined style={{ fontSize: 48, color: '#2dce89' }} />
              </div>
              <Text type="secondary">二维码示意图</Text>
            </div>
            <Button 
              type="primary" 
              onClick={() => {
                setWechatQRVisible(false);
                message.success('微信登录成功');
              }}
            >
              模拟登录成功
            </Button>
          </Card>
        </div>
      </Modal>
      
      {/* 系统设置模态框 */}
      <Modal
        title="系统设置"
        open={settingsModalVisible}
        onCancel={() => setSettingsModalVisible(false)}
        footer={null}
        width={600}
        className="settings-modal"
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Tabs 
          activeKey={settingsActiveTab} 
          onChange={setSettingsActiveTab}
          centered
          items={[
            {
              key: 'general',
              label: t('settings.general'),
              children: (
                <Form
                  form={generalForm}
                  layout="vertical"
                  onFinish={handleGeneralSettings}
                  initialValues={{
                    nickname: user?.user_metadata?.nickname || '',
                    company: user?.user_metadata?.company || '',
                    phone: user?.user_metadata?.phone || '',
                    smsCode: '',
                  }}
                >
                  <Form.Item
                    name="nickname"
                    label="昵称"
                    rules={[{ required: true, message: '请输入昵称!' }]}
                  >
                    <Input placeholder="请输入昵称" />
                  </Form.Item>
                  
                  <Form.Item
                    name="company"
                    label="单位名称"
                  >
                    <Input placeholder="请输入单位名称" />
                  </Form.Item>
                  
                  <Form.Item
                    name="phone"
                    label="联系电话"
                    rules={[
                      { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码!' }
                    ]}
                  >
                    <Input
                      placeholder="请输入手机号码"
                      maxLength={13}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\s/g, '');
                        // 11位手机按 3-4-4 分组
                        const fmt = raw.replace(/(\d{3})(\d{0,4})(\d{0,4})/, (m, a, b, c) => [a, b, c].filter(Boolean).join(' '));
                        generalForm.setFieldsValue({ phone: fmt });
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="smsCode"
                    label="短信验证码"
                    // 改为可选：不再强制要求验证码
                  >
                    <Space.Compact style={{ width: '100%' }}>
                      <Input placeholder="验证码" maxLength={6} />
                      <Button onClick={sendSmsCode} disabled={!online || smsCountdown > 0 || smsSending}>
                        {smsCountdown > 0 ? `${smsCountdown}s后重发` : '发送验证码'}
                      </Button>
                    </Space.Compact>
                  </Form.Item>
                  
                  <Form.Item>
                    <Button type="primary" htmlType="submit" block disabled={!online}>
                      保存设置
                    </Button>
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'ai',
              label: t('settings.ai') || 'AI 模型设置',
              children: (
                <Form
                  form={aiForm}
                  layout="vertical"
                  onFinish={handleAiSettings}
                  initialValues={getAiOptions()}
                >
                  <Form.Item
                    name="endpointUrl"
                    label={t('ai.settings.endpoint_url') || '接口地址'}
                    rules={[{ required: true, message: '请输入接口地址' }]}
                  >
                    <Input placeholder="例如 https://api.openai.com/v1/chat/completions" />
                  </Form.Item>
                  <Form.Item
                    name="apiKey"
                    label={t('ai.settings.api_key') || 'API Key'}
                  >
                    <Input.Password placeholder="用于鉴权的Key" />
                  </Form.Item>
                  <Form.Item
                    name="model"
                    label={t('ai.settings.model') || '模型名称'}
                  >
                    <Input placeholder="例如 gpt-4o-mini" />
                  </Form.Item>
                  <Form.Item
                    name="temperature"
                    label={t('ai.settings.temperature') || '生成温度'}
                  >
                    <InputNumber min={0} max={1} step={0.1} />
                  </Form.Item>
                  <Form.Item
                    name="maxTokens"
                    label={t('ai.settings.max_tokens') || '最大Tokens'}
                  >
                    <InputNumber min={100} max={4000} step={100} />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" block>
                      保存
                    </Button>
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'password',
              label: t('settings.password'),
              children: (
                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handlePasswordChange}
                >
                  <Form.Item
                    name="currentPassword"
                    label="当前密码"
                    rules={[{ required: true, message: '请输入当前密码!' }]}
                  >
                    <Input.Password placeholder="请输入当前密码" />
                  </Form.Item>
                  
                  <Form.Item
                    name="newPassword"
                    label="新密码"
                    rules={[
                      { required: true, message: '请输入新密码!' },
                      { min: 6, message: '密码至少6位!' }
                    ]}
                  >
                    <Input.Password placeholder="请输入新密码" />
                  </Form.Item>
                  
                  <Form.Item
                    name="confirmPassword"
                    label="确认新密码"
                    rules={[
                      { required: true, message: '请确认新密码!' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('两次输入的密码不一致!'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="请确认新密码" />
                  </Form.Item>
                  
                  <Form.Item>
                    <Button type="primary" htmlType="submit" block disabled={!online}>
                      修改密码
                    </Button>
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'calculation',
              label: t('settings.calculation'),
              children: (
                <Form
                  form={calculationForm}
                  layout="vertical"
                  onFinish={handleCalculationSettings}
                  initialValues={{
                    defaultProtectionType: 'overcurrent',
                    defaultSafetyFactor: 1.2,
                    defaultCalculationMethod: 'iec60255',
                  }}
                >
                  <Form.Item
                    name="defaultProtectionType"
                    label="默认保护类型"
                  >
                    <Input placeholder="默认保护类型" />
                  </Form.Item>
                  
                  <Form.Item
                    name="defaultSafetyFactor"
                    label="默认安全系数"
                    rules={[
                      { required: true, message: '请输入安全系数!' },
                      { type: 'number', min: 1.0, max: 3.0, message: '安全系数应在1.0-3.0之间!' }
                    ]}
                  >
                    <Input type="number" step="0.1" placeholder="请输入安全系数" />
                  </Form.Item>
                  
                  <Form.Item
                    name="defaultCalculationMethod"
                    label="默认计算方法"
                  >
                    <Input placeholder="默认计算方法" />
                  </Form.Item>
                  
                  <Form.Item>
                    <Button type="primary" htmlType="submit" block>
                      保存计算参数
                    </Button>
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'about',
              label: t('settings.about'),
              children: (
                <div>
                  <Typography.Title level={4}>关于系统</Typography.Title>
                  <Typography.Paragraph>
                    <strong>系统名称：</strong>{appTitle}
                  </Typography.Paragraph>
                  <Typography.Paragraph>
                    <strong>当前版本：</strong>{appVersion}
                  </Typography.Paragraph>
                  <Divider />
                  <Typography.Title level={5}>修改记录</Typography.Title>
                  <div style={{ maxHeight: '50vh', overflowY: 'auto', paddingRight: 8 }}>
                    <Timeline>
                      {CHANGELOG.map((entry, idx) => (
                        <Timeline.Item key={idx}>
                          <div>
                            <strong>{entry.version}</strong> {entry.date ? `（${entry.date}）` : ''}
                          </div>
                          <ul style={{ margin: '4px 0 0 16px' }}>
                            {entry.changes.map((c, i) => (
                              <li key={i}>{c}</li>
                            ))}
                          </ul>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </div>
                </div>
              )
            }
          ]}
        />
      </Modal>
      {import.meta.env.VITE_ENABLE_INSIGHTS === 'true' && <Analytics />}
    </Layout>
  );
};

// 主应用组件
const MainApp = () => (
  <HashRouter>
    <App />
  </HashRouter>
);

export default MainApp;
